import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Loader2, Check, X, ShieldAlert, ShieldCheck, Mail, ChevronDown, ChevronUp,
    RefreshCw, Download, AlertTriangle, CheckCircle2, XCircle, Copy, FileImage,
    Trash2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import ArchivedFilterToggle, { ArchivedRowBadge } from "@/components/ArchivedFilterToggle";

const RISK_TIERS = [
    { max: 29, label: "LOW", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
    { max: 59, label: "MEDIUM", cls: "bg-amber-100 text-amber-900 border-amber-200" },
    { max: 100, label: "HIGH", cls: "bg-rose-100 text-rose-800 border-rose-200" },
];

const STATUS_PILL = {
    pending_invite: "bg-zinc-100 text-zinc-700 border-zinc-200",
    link_sent: "bg-sky-100 text-sky-800 border-sky-200",
    in_progress: "bg-indigo-100 text-indigo-800 border-indigo-200",
    awaiting_review: "bg-amber-100 text-amber-900 border-amber-200",
    approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rejected: "bg-rose-100 text-rose-800 border-rose-200",
    expired: "bg-rose-50 text-rose-700 border-rose-300 border-dashed",
};
const STATUS_LABEL = {
    pending_invite: "Pending invite",
    link_sent: "Link sent",
    in_progress: "In progress",
    awaiting_review: "Awaiting review",
    approved: "Verified",
    rejected: "Rejected",
    expired: "Link expired",
};

export default function AdminRecruiting() {
    const { user } = useAuth();
    const isAdminOrMaster = ["master_admin", "admin"].includes(user?.role);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verByApp, setVerByApp] = useState({});       // { applicant_id: verification | null | "loading" }
    const [expandedId, setExpandedId] = useState(null);
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);

    const loadRows = useCallback(() => {
        setLoading(true);
        adminApi.recruiting({ include_deleted: includeDeleted || undefined })
            .then(setRows)
            .catch(() => toast.error("Couldn't load recruiting."))
            .finally(() => setLoading(false));
    }, [includeDeleted]);
    useEffect(() => { loadRows(); }, [loadRows]);

    const loadVer = useCallback(async (appId) => {
        setVerByApp((m) => ({ ...m, [appId]: "loading" }));
        try {
            const v = await adminApi.getApplicantIdVerification(appId);
            setVerByApp((m) => ({ ...m, [appId]: v || null }));
        } catch {
            setVerByApp((m) => ({ ...m, [appId]: null }));
        }
    }, []);

    useEffect(() => {
        // Preload verification status for each approved applicant so the
        // table can show the right badge without a click.
        rows.filter((r) => r.status === "approved").forEach((r) => {
            if (verByApp[r.id] === undefined) loadVer(r.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows]);

    const update = async (id, body) => {
        try {
            await adminApi.patchRecruiting(id, body);
            toast.success("Updated.");
            loadRows();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update.");
        }
    };

    const toggleExpanded = (appId) => {
        setExpandedId((cur) => (cur === appId ? null : appId));
        if (verByApp[appId] === undefined) loadVer(appId);
    };

    return (
        <div className="space-y-5" data-testid="admin-recruiting">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="mm-eyebrow">Recruiting</div>
                    <h1 className="mm-display text-3xl text-mm-navy">Applications</h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        Applicants without an active 215 license cannot be approved. After Brent approves, send them an ID verification link.
                    </p>
                </div>
                {isAdminOrMaster && (
                    <ArchivedFilterToggle
                        value={includeDeleted}
                        onChange={setIncludeDeleted}
                        testid="recruit-archived-toggle"
                    />
                )}
            </header>
            <div className="mm-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-mm-bg text-[11px] uppercase tracking-wider text-mm-muted">
                            <tr>
                                <Th>Name</Th>
                                <Th>Email</Th>
                                <Th>License</Th>
                                <Th>State</Th>
                                <Th>Exp</Th>
                                <Th>Status</Th>
                                <Th>ID Verification</Th>
                                <Th>Actions</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="py-10 text-center"><Loader2 className="mx-auto animate-spin text-mm-navy" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="py-10 text-center text-mm-muted">No applications yet.</td></tr>
                            ) : (
                                rows.map((r) => {
                                    const ver = verByApp[r.id];
                                    const isExpanded = expandedId === r.id;
                                    const licenseBlocked = !r.license_active || r.license_type !== "215";
                                    const archived = !!r.deleted_at;
                                    return (
                                        <React.Fragment key={r.id}>
                                            <tr
                                                className={`border-t border-mm-line ${isExpanded ? "bg-mm-bg/40" : ""} ${archived ? "opacity-60" : ""}`}
                                                data-testid={`recruit-row-${r.id}`}
                                            >
                                                <Td>
                                                    <div className="flex items-center gap-2">
                                                        <strong>{r.name}</strong>
                                                        {archived && <ArchivedRowBadge deletedAt={r.deleted_at} testid={`recruit-archived-badge-${r.id}`} />}
                                                    </div>
                                                </Td>
                                                <Td>{r.email}</Td>
                                                <Td>{r.license_number || "—"}</Td>
                                                <Td>{r.license_state || "—"}</Td>
                                                <Td>{r.experience_years ?? 0} yr</Td>
                                                <Td>
                                                    {licenseBlocked ? (
                                                        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-800" data-testid={`recruit-blocked-${r.id}`}>
                                                            <ShieldAlert size={11} /> Blocked
                                                        </span>
                                                    ) : (
                                                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${
                                                            r.status === "approved" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                                            : r.status === "rejected" ? "border-rose-200 bg-rose-50 text-rose-800"
                                                            : "border-amber-200 bg-amber-50 text-amber-900"
                                                        }`}>{r.status}</span>
                                                    )}
                                                </Td>
                                                <Td>
                                                    <IdVerBadge applicantStatus={r.status} ver={ver} />
                                                </Td>
                                                <Td>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            onClick={() => update(r.id, { status: "approved" })}
                                                            disabled={licenseBlocked || r.status === "approved"}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40"
                                                            title={licenseBlocked ? "Blocked — no active 215 license" : "Approve"}
                                                            data-testid={`recruit-approve-${r.id}`}
                                                        >
                                                            <Check size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => update(r.id, { status: "rejected" })}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                                            title="Reject"
                                                            data-testid={`recruit-reject-${r.id}`}
                                                        >
                                                            <X size={13} />
                                                        </button>
                                                        <button
                                                            onClick={() => toggleExpanded(r.id)}
                                                            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-mm-line bg-white text-mm-navy hover:bg-mm-bg"
                                                            title={isExpanded ? "Collapse" : "ID Verification"}
                                                            data-testid={`recruit-expand-${r.id}`}
                                                        >
                                                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                                                        </button>
                                                        {isAdminOrMaster && !archived && (
                                                            <button
                                                                onClick={() => setPendingDelete({ id: r.id, name: r.name })}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-mm-line bg-white text-red-600 hover:bg-red-50"
                                                                title="Archive"
                                                                data-testid={`recruit-delete-${r.id}`}
                                                            >
                                                                <Trash2 size={13} />
                                                            </button>
                                                        )}
                                                        {isAdminOrMaster && archived && (
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        await adminApi.restore("recruiting", r.id);
                                                                        toast.success("Restored.");
                                                                        loadRows();
                                                                    } catch { toast.error("Couldn't restore."); }
                                                                }}
                                                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                                                title="Restore"
                                                                data-testid={`recruit-restore-${r.id}`}
                                                            >
                                                                <RotateCcw size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </Td>
                                            </tr>
                                            {isExpanded && (
                                                <tr className="border-t border-mm-line bg-[#F4F8FC]/40">
                                                    <td colSpan={8} className="px-4 py-5">
                                                        {(r.upline_first_name || r.upline_last_name) && (
                                                            <div
                                                                className="mb-3 rounded-lg border border-mm-line bg-white px-3 py-2 text-[12px] text-mm-ink"
                                                                data-testid={`recruit-declared-upline-${r.id}`}
                                                            >
                                                                <span className="text-mm-muted">Declared upline: </span>
                                                                <strong className="text-mm-navy">
                                                                    {r.upline_first_name} {r.upline_last_name}
                                                                </strong>
                                                                <span className="ml-2 text-[10px] text-mm-muted italic">
                                                                    (free-text; assign the real user when sending the invite)
                                                                </span>
                                                            </div>
                                                        )}
                                                        <IdVerificationPanel
                                                            applicant={r}
                                                            ver={ver}
                                                            onChanged={() => loadVer(r.id)}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmDeleteModal
                open={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={async (reason) => {
                    try {
                        await adminApi.softDelete("recruiting", pendingDelete.id, reason);
                        toast.success("Application archived. Restore within 30 days.");
                        setPendingDelete(null);
                        loadRows();
                    } catch { toast.error("Couldn't archive."); }
                }}
                resourceLabel="recruiting application"
                resourceName={pendingDelete?.name}
                testidPrefix="recruit-confirm-delete"
            />
        </div>
    );
}

function Th({ children }) { return <th className="px-3 py-2.5 text-left font-medium">{children}</th>; }
function Td({ children, className = "" }) {
    return <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
}

/* ---------------- Id verification badge in the list ---------------- */

function IdVerBadge({ applicantStatus, ver }) {
    if (applicantStatus !== "approved") {
        return <span className="text-[11px] text-mm-muted">—</span>;
    }
    if (ver === undefined || ver === "loading") {
        return <Loader2 size={12} className="animate-spin text-mm-muted" />;
    }
    if (!ver) {
        return <span className="text-[11px] text-mm-muted italic">Not started</span>;
    }
    const isHighRisk = (ver.claude_analysis?.fraud_risk_score || 0) >= 60;
    return (
        <div className="flex items-center gap-1.5">
            <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[ver.status] || ""}`}
                data-testid={`idver-status-${ver.id}`}
            >
                {ver.status === "approved" && <ShieldCheck size={10} />}
                {STATUS_LABEL[ver.status] || ver.status}
            </span>
            {isHighRisk && ver.status === "awaiting_review" && (
                <span
                    className="inline-flex items-center gap-0.5 rounded-full border border-rose-300 bg-rose-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                    title="High fraud risk — review immediately"
                    data-testid={`idver-highrisk-${ver.id}`}
                >
                    <AlertTriangle size={9} /> HIGH
                </span>
            )}
        </div>
    );
}

/* ---------------- Expanded ID verification panel ---------------- */

function IdVerificationPanel({ applicant, ver, onChanged }) {
    const [sending, setSending] = useState(false);
    const [lightbox, setLightbox] = useState(null); // { slot, src, filename }
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [deciding, setDeciding] = useState(false);

    if (applicant.status !== "approved") {
        return (
            <div className="text-sm text-mm-muted">
                Brent needs to approve this application first. Once approved, you can send an ID verification link here.
            </div>
        );
    }

    const sendLink = async () => {
        setSending(true);
        try {
            const res = await adminApi.sendIdVerification(applicant.id);
            toast.success("Verification email sent.");
            onChanged?.();
            return res;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send.");
        } finally {
            setSending(false);
        }
    };

    const regenerate = async () => {
        if (!ver?.id) return;
        setSending(true);
        try {
            await adminApi.regenerateIdVerificationLink(ver.id);
            toast.success("Fresh link sent.");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't regenerate.");
        } finally {
            setSending(false);
        }
    };

    const approve = async () => {
        if (!ver?.id) return;
        setDeciding(true);
        try {
            await adminApi.approveIdVerification(ver.id);
            toast.success(`${applicant.name.split(" ")[0]} is verified.`);
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't approve.");
        } finally {
            setDeciding(false);
        }
    };

    const reject = async () => {
        if (!ver?.id || rejectReason.trim().length < 3) return;
        setDeciding(true);
        try {
            await adminApi.rejectIdVerification(ver.id, rejectReason.trim());
            toast.success("Rejection sent. Retry link shared with the applicant.");
            setRejectOpen(false);
            setRejectReason("");
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't reject.");
        } finally {
            setDeciding(false);
        }
    };

    // No verification yet
    if (!ver) {
        return (
            <div className="flex flex-col gap-3 text-sm">
                <div className="mm-eyebrow">Identity Verification</div>
                <p className="text-mm-muted">
                    Send {applicant.name.split(" ")[0]} a polished welcome email with a one-tap "Verify Identification" link. Expires in 7 days.
                </p>
                <div>
                    <button
                        onClick={sendLink}
                        disabled={sending}
                        className="mm-btn-primary disabled:opacity-60"
                        data-testid={`idver-send-${applicant.id}`}
                    >
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                        {sending ? "Sending…" : "Send ID Verification Email"}
                    </button>
                </div>
                <p className="text-[11px] italic text-mm-muted">
                    Moving Mountains will never ask for payment information during onboarding.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4 text-sm" data-testid={`idver-panel-${ver.id}`}>
            <div className="flex items-center justify-between gap-2">
                <div>
                    <div className="mm-eyebrow">Identity Verification</div>
                    <h3 className="mm-display text-lg text-mm-navy">
                        {STATUS_LABEL[ver.status] || ver.status}
                    </h3>
                </div>
                <div className="flex items-center gap-1.5">
                    {ver.link && (
                        <CopyLinkButton link={ver.link} />
                    )}
                    {["link_sent", "in_progress", "expired", "rejected"].includes(ver.status) && (
                        <button
                            onClick={regenerate}
                            disabled={sending}
                            className="mm-btn-outline !py-1.5 text-xs disabled:opacity-60"
                            data-testid={`idver-regenerate-${ver.id}`}
                        >
                            <RefreshCw size={12} /> Regenerate link
                        </button>
                    )}
                </div>
            </div>

            {(ver.status === "link_sent" || ver.status === "in_progress" || ver.status === "expired") && (
                <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2.5 text-xs text-sky-900">
                    Waiting for {applicant.name.split(" ")[0]} to upload.
                    {ver.link_expires_at && <> Link expires <strong>{new Date(ver.link_expires_at).toLocaleString()}</strong>.</>}
                </div>
            )}

            {(ver.status === "awaiting_review" || ver.status === "approved" || ver.status === "rejected") && ver.files && (
                <div className="grid gap-4 md:grid-cols-2">
                    <FileTile
                        title={idTypeLabel(ver.id_type) + " (ID Document)"}
                        slot="id_document"
                        verId={ver.id}
                        filename={ver.files.id_document?.filename}
                        onOpen={(src) => setLightbox({ slot: "id_document", src, filename: ver.files.id_document?.filename })}
                    />
                    <FileTile
                        title="Selfie"
                        slot="selfie"
                        verId={ver.id}
                        filename={ver.files.selfie?.filename}
                        onOpen={(src) => setLightbox({ slot: "selfie", src, filename: ver.files.selfie?.filename })}
                    />
                </div>
            )}

            {ver.claude_analysis && (
                <ClaudeAnalysisCard
                    ver={ver}
                    applicant={applicant}
                />
            )}

            {ver.status === "awaiting_review" && (
                <div className="flex items-center gap-2 border-t border-mm-line pt-3">
                    <button
                        onClick={approve}
                        disabled={deciding || !ver.claude_analysis}
                        className="mm-btn-primary disabled:opacity-50"
                        data-testid={`idver-approve-${ver.id}`}
                        style={{ background: "#0A6E43" }}
                    >
                        {deciding ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        Approve
                    </button>
                    <button
                        onClick={() => setRejectOpen(true)}
                        disabled={deciding}
                        className="mm-btn-outline disabled:opacity-50"
                        data-testid={`idver-reject-${ver.id}`}
                        style={{ color: "#9F1239", borderColor: "#FECDD3" }}
                    >
                        <XCircle size={14} /> Reject
                    </button>
                </div>
            )}

            {rejectOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4" role="dialog" data-testid="reject-modal">
                    <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
                        <h3 className="mm-display text-xl text-mm-navy">Reject verification</h3>
                        <p className="mt-1 text-xs text-mm-muted">
                            Share a short, kind reason. The applicant will receive a polite retry email with a fresh link.
                        </p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            rows={4}
                            placeholder="E.g. ID photo was blurry — please retake in natural light with all four corners visible."
                            className="mt-3 w-full rounded-md border border-mm-line p-2.5 text-sm focus:border-mm-navy focus:outline-none"
                            data-testid="reject-reason-input"
                        />
                        <div className="mt-3 flex items-center justify-end gap-2">
                            <button
                                onClick={() => { setRejectOpen(false); setRejectReason(""); }}
                                className="mm-btn-outline"
                                disabled={deciding}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={reject}
                                disabled={deciding || rejectReason.trim().length < 3}
                                className="mm-btn-primary disabled:opacity-50"
                                style={{ background: "#9F1239" }}
                                data-testid="reject-confirm-btn"
                            >
                                {deciding ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                                Send rejection
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {lightbox && (
                <ImageLightbox
                    src={lightbox.src}
                    filename={lightbox.filename}
                    onClose={() => setLightbox(null)}
                />
            )}
        </div>
    );
}

function idTypeLabel(t) {
    return { drivers_license: "Driver's License", passport: "Passport", state_id: "State ID" }[t] || t || "ID";
}

function CopyLinkButton({ link }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success("Link copied.");
            setTimeout(() => setCopied(false), 1200);
        } catch {
            toast.error("Copy failed.");
        }
    };
    return (
        <button
            onClick={copy}
            className="mm-btn-outline !py-1.5 text-xs"
            title={link}
            data-testid="idver-copy-link"
        >
            <Copy size={12} /> {copied ? "Copied" : "Copy link"}
        </button>
    );
}

function FileTile({ title, slot, verId, filename, onOpen }) {
    const src = adminApi.idVerificationFileUrl(verId, slot);
    const isPdf = (filename || "").toLowerCase().endsWith(".pdf");
    return (
        <div className="rounded-xl border border-mm-line bg-white p-3" data-testid={`idver-file-tile-${slot}`}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider text-mm-muted">{title}</div>
                <a
                    href={src}
                    download={filename || slot}
                    className="inline-flex items-center gap-1 text-xs text-mm-navy hover:underline"
                    data-testid={`idver-download-${slot}`}
                >
                    <Download size={12} /> Download
                </a>
            </div>
            {isPdf ? (
                <a
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 flex h-40 items-center justify-center rounded-md border border-mm-line bg-mm-bg text-sm text-mm-muted"
                >
                    <FileImage size={24} className="mr-2" /> PDF — open in new tab
                </a>
            ) : (
                <button
                    onClick={() => onOpen(src)}
                    className="mt-2 block w-full"
                    data-testid={`idver-thumb-${slot}`}
                >
                    <img
                        src={src}
                        alt={title}
                        className="h-40 w-full rounded-md object-cover transition-transform hover:scale-[1.01]"
                    />
                </button>
            )}
        </div>
    );
}

function ImageLightbox({ src, filename, onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
            onClick={onClose}
            data-testid="idver-lightbox"
        >
            <img
                src={src}
                alt={filename || "enlarged"}
                className="max-h-[90vh] max-w-[95vw] rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            />
            <button
                onClick={onClose}
                className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
                aria-label="Close"
            >
                <X size={16} />
            </button>
        </div>
    );
}

function ClaudeAnalysisCard({ ver, applicant }) {
    const a = ver.claude_analysis || {};
    const risk = a.fraud_risk_score ?? 0;
    const tier = RISK_TIERS.find((t) => risk <= t.max) || RISK_TIERS[RISK_TIERS.length - 1];
    const nameMatch = ver.name_match_with_application;
    const [reasoningOpen, setReasoningOpen] = useState(false);

    const extracted = a.extracted_fields || ver.extracted_fields || {};
    const flags = a.flags || [];

    return (
        <div className="rounded-xl border border-mm-line bg-white p-4" data-testid={`idver-analysis-${ver.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className="mm-eyebrow">Claude Vision analysis</div>
                    <p className="text-[11px] text-mm-muted">Advisory — admin makes the final call.</p>
                </div>
                <div className="flex items-center gap-2">
                    <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tier.cls}`}
                        data-testid={`idver-risk-badge-${ver.id}`}
                    >
                        {tier.label} · {risk}/100
                    </span>
                    {nameMatch ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-800">
                            <CheckCircle2 size={10} /> Name match
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-800">
                            <XCircle size={10} /> Name mismatch
                        </span>
                    )}
                </div>
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2">
                <div className="rounded-md bg-mm-bg/60 p-3">
                    <div className="mm-eyebrow mb-1.5">Extracted fields</div>
                    <KV k="Name on ID" v={extracted.full_name} />
                    <KV k="DOB" v={extracted.dob} />
                    <KV k="ID #" v={extracted.id_number} />
                    <KV k="Expires" v={extracted.expiration_date} />
                    <KV k="Issuing state" v={extracted.issuing_state_or_country} />
                    <KV k="Address" v={extracted.address} />
                    <KV k="Applicant on file" v={applicant.name} />
                </div>
                <div className="rounded-md bg-mm-bg/60 p-3">
                    <div className="mm-eyebrow mb-1.5">Signals</div>
                    <div className="mb-2">
                        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-mm-muted">
                            <span>Face match</span>
                            <span>{a.face_match_confidence ?? "—"}%</span>
                        </div>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white">
                            <div
                                className="h-full rounded-full"
                                style={{
                                    width: `${Math.max(0, Math.min(100, a.face_match_confidence || 0))}%`,
                                    background: "linear-gradient(90deg,#1E6FD9,#C9A84C)",
                                }}
                            />
                        </div>
                    </div>
                    {flags.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1">
                            {flags.map((f) => (
                                <span key={f} className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-800">{f}</span>
                            ))}
                        </div>
                    )}
                    {(a.tampering_signals || []).length > 0 && (
                        <div className="mb-1">
                            <div className="text-[10px] uppercase tracking-wider text-mm-muted">Tampering signals</div>
                            <ul className="mt-1 list-disc pl-4 text-xs text-mm-ink">
                                {a.tampering_signals.map((t, i) => <li key={i}>{t}</li>)}
                            </ul>
                        </div>
                    )}
                    {(a.liveness_signals || []).length > 0 && (
                        <div className="text-[11px] text-mm-muted">
                            Liveness: {a.liveness_signals.join(" · ")}
                        </div>
                    )}
                </div>
            </div>

            {a.reasoning && (
                <div className="mt-3 rounded-md bg-mm-bg/40 p-3">
                    <button
                        onClick={() => setReasoningOpen((v) => !v)}
                        className="flex w-full items-center justify-between text-[11px] font-medium text-mm-navy"
                    >
                        <span>Reasoning</span>
                        {reasoningOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    {reasoningOpen && <p className="mt-2 text-xs text-mm-ink">{a.reasoning}</p>}
                </div>
            )}

            <p className="mt-3 text-[11px] italic text-mm-muted">
                Moving Mountains will never ask for payment information during onboarding.
            </p>
        </div>
    );
}

function KV({ k, v }) {
    return (
        <div className="flex items-center justify-between py-0.5 text-xs">
            <span className="text-mm-muted">{k}</span>
            <span className="font-medium text-mm-ink truncate ml-2">{v || "—"}</span>
        </div>
    );
}
